import { apiService } from './apiService.js';

class AgentService {
  getBaseURL() {
    return apiService.baseURL || '';
  }

  async getAgents(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      // Extract the agents array from the response object
      result.data = responseData.agents || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch agents' }));
    }

    return result;
  }

  async createAgent(token, agentData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(agentData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.agent || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to create agent' }));
    }

    return result;
  }

  async updateAgent(token, agentId, agentData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/${agentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(agentData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.agent || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to update agent' }));
    }

    return result;
  }

  async getAgentConnections(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/connections`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.connections || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch agent connections' }));
    }

    return result;
  }

  async createAgentConnection(token, connectionData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/connections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(connectionData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.connection || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to create agent connection' }));
    }

    return result;
  }

  async deleteAgentConnection(token, connectionId) {
    const response = await apiService.authenticatedFetch(
      `${this.getBaseURL()}/api/agents/connections/${connectionId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      result.data = await response.json();
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to delete agent connection' }));
    }

    return result;
  }

  async getMcpServers(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/mcp-servers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.servers || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch MCP servers' }));
    }

    return result;
  }

  async getAgentMcpConnections(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/mcp-connections`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.connections || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch agent MCP connections' }));
    }

    return result;
  }

  async createAgentMcpConnection(token, connectionData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/mcp-connections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(connectionData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.connection || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to create agent MCP connection' }));
    }

    return result;
  }

  async deleteAgentMcpConnection(token, agentId, mcpServerId) {
    const response = await apiService.authenticatedFetch(
      `${this.getBaseURL()}/api/agents/mcp-connections/${agentId}/${mcpServerId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      result.data = await response.json();
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to delete agent MCP connection' }));
    }

    return result;
  }

  async getMediaBuyAgent(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/media-buy`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to fetch media-buy agent' })) : null,
    };
  }

  async getAgent(token, agentId) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/${agentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to fetch agent' })) : null,
    };
  }

  async getAgentCategories(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/categories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      // Extract the categories array from the response object
      result.data = responseData.categories || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch agent categories' }));
    }

    return result;
  }

  // ==================== Trigger API Methods ====================

  async getAgentTriggers(token, agentId) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/${agentId}/triggers`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.triggers || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch agent triggers' }));
    }

    return result;
  }

  async createTrigger(token, triggerData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/triggers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(triggerData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.trigger || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to create trigger' }));
    }

    return result;
  }

  async updateTrigger(token, triggerId, triggerData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/triggers/${triggerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(triggerData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.trigger || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to update trigger' }));
    }

    return result;
  }

  async deleteTrigger(token, triggerId) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/triggers/${triggerId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      result.data = await response.json();
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to delete trigger' }));
    }

    return result;
  }

  async testTrigger(token, triggerId, payload = {}) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/triggers/${triggerId}/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      result.data = await response.json();
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to test trigger' }));
    }

    return result;
  }

  async regenerateTriggerSecret(token, triggerId) {
    const response = await apiService.authenticatedFetch(
      `${this.getBaseURL()}/api/triggers/${triggerId}/regenerate-secret`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      result.data = await response.json();
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to regenerate secret' }));
    }

    return result;
  }

  async getTriggerExecutions(token, triggerId) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/triggers/${triggerId}/executions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.executions || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch trigger executions' }));
    }

    return result;
  }

  async getNextRuns(token, triggerId, count = 10) {
    const response = await apiService.authenticatedFetch(
      `${this.getBaseURL()}/api/triggers/${triggerId}/next-runs?count=${count}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch next runs' }));
    }

    return result;
  }

  // ==================== TAGS ====================

  async getTags(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/tags`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.tags || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch tags' }));
    }

    return result;
  }

  async createTag(token, tagData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/tags`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(tagData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.tag || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to create tag' }));
    }

    return result;
  }

  async setAgentTags(token, agentId, tagIds) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/${agentId}/tags`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tagIds }),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.agent || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to set agent tags' }));
    }

    return result;
  }

  async setMcpServerTags(token, serverId, tagIds) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/mcp-servers/${serverId}/tags`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tagIds }),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.server || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to set MCP server tags' }));
    }

    return result;
  }

  // ==================== RAG Corpora ====================

  async getRagCorpora(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/rag-corpora`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.ragCorpora || responseData.corpora || [];
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch RAG corpora' }));
    }

    return result;
  }

  async getAgentRagConnections(token) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/rag-connections`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.connections || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to fetch agent RAG connections' }));
    }

    return result;
  }

  async createAgentRagConnection(token, connectionData) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/agents/rag-connections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(connectionData),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.connection || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to create agent RAG connection' }));
    }

    return result;
  }

  async deleteAgentRagConnection(token, agentId, ragCorpusId) {
    const response = await apiService.authenticatedFetch(
      `${this.getBaseURL()}/api/agents/rag-connections/${agentId}/${ragCorpusId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      result.data = await response.json();
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to delete agent RAG connection' }));
    }

    return result;
  }

  async setRagCorpusTags(token, corpusId, tagIds) {
    const response = await apiService.authenticatedFetch(`${this.getBaseURL()}/api/rag-corpora/${corpusId}/tags`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ tagIds }),
    });

    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: null,
      error: null,
    };

    if (response.ok) {
      const responseData = await response.json();
      result.data = responseData.corpus || responseData;
    } else {
      result.error = await response.json().catch(() => ({ message: 'Failed to set RAG corpus tags' }));
    }

    return result;
  }
}

export const agentService = new AgentService();
export default agentService;
